import { useState } from 'react';
import { getFilterData } from '../api/nicePayments.api';
import iconv from 'iconv-lite';

const FS = '\x1C'; // Field Separator

// Helper Functions
const getByteLength = (str: string): number => {
  let l = 0;
  for (let i = 0; i < str.length; i++) {
    const c = escape(str.charAt(i));
    if (c.length === 1) l++;
    else if (c.indexOf('%u') !== -1) l += 3;
    else if (c.indexOf('%') !== -1) l += c.length / 3;
  }
  return l;
};

const padNumber = (value: string | number, width: number): string => {
  const str = value.toString();
  return str.length >= width ? str : new Array(width - str.length + 1).join('0') + str;
};

const makeSendData = (type: string, sendData: string): string => {
  const bodyLen = getByteLength(sendData);
  const totalLen = 12 + bodyLen;
  return padNumber(totalLen, 4) + type + '    ' + padNumber(bodyLen, 4) + sendData;
};

// Define the response structure
export interface ParsedResponse {
  success: boolean;
  oid: number;
  type: 'P' | 'R';
  posCode: number;
  message: string;
  response?: string;
  request?: string;
  serviceCode?: string;
  rejectCode?: string;
  rejectMessage1?: string;
  rejectMessage2?: string;
  transactionDateTime?: string;
  transactionDate?: string;
  totalAmount?: string;
  vatAmount?: string;
  approvalNumber?: string;
  issuerCode?: string;
  acquirerCode?: string;
  issuerName?: string;
  acquirerName?: string;
  merchantId?: string;
  maskedCardNumber?: string;
  installmentMonths?: string;
  processingNumber?: string;
  catId?: string;
}

const parseResponse = (type: 'approve' | 'cancel', orderId: number, request: string, response: string): ParsedResponse => {
  const bodyLen = parseInt(response.substr(12, 4), 10);
  const errorCode = response.substr(8, 4);
  const body = response.substr(16, bodyLen);

  if (errorCode !== '0000') {
    return {
      success: false,
      type: type === 'approve' ? 'P' : 'R',
      oid: orderId,
      posCode: parseInt(errorCode, 10) * -1,
      message: mapErrorCode(parseInt(errorCode, 10) * -1),
      request,
    };
  }

  const fields = body.split('\x1C'); // FS separator

  return {
    success: fields[1] === 'A',
    type: type === 'approve' ? 'P' : 'R',
    oid: orderId,
    posCode: 1,
    message: 'Transaction Successful',
    response: body,
    request: request,
    serviceCode: fields[0]?.replace(/\u0002/g, '').trim(), // 🔥 STX 제거 및 공백 처리
    rejectCode: fields[1],
    rejectMessage1: fields[2],
    rejectMessage2: fields[3],
    transactionDateTime: fields[4],
    transactionDate: fields[4]?.substring(0, 8), // 날짜만 (YYMMDD)
    totalAmount: fields[5],
    vatAmount: fields[6],
    approvalNumber: fields[7],
    issuerCode: fields[8],
    acquirerCode: fields[9],
    issuerName: fields[10],
    acquirerName: fields[11],
    merchantId: fields[12],
    maskedCardNumber: fields[13],
    installmentMonths: fields[14],
    processingNumber: fields[15],
    catId: fields[16],
  };
};

const mapErrorCode = (code: number): string => {
  const errorMap: { [key: number]: string } = {
    '-1': 'Port open failed',
    '-2': 'Port already open',
    '-3': 'ACK receive failed (Terminal connection failed)',
    '-4': 'LRC error or termination button pressed',
    '-14': 'Invalid request message format',
    '-99': 'GET not available',
  };
  return errorMap[code] || `error code: ${code}`;
};

const createTransactionMessage = (
  serviceCode: string,
  totalAmount: number,
  installmentMonths: string,
  taxAmount: number,
  approvalNumber: string = '',
  originalDate: string = '',
  filterData: string,
  catId?: string,
): string => {
  const formattedInstallments = padNumber(installmentMonths || 0, 2);

  if (catId) {
    return (
      serviceCode + FS + // #2 Service Code
      FS + // #4 #5 서명데이타 요청 여부
      FS + // #6 #7 무카드(고유번호)취소 번호
      formattedInstallments + FS + // #8 #9 할부개월
      FS + // #10 #11 면세 금액
      taxAmount + FS + // #12 #13 과세 금액
      totalAmount + FS + // #14 #15 총 판매 금액
      (approvalNumber || '') + FS + // #16 #17 승인번호
      (originalDate || '') + FS + // #18 #19 원거래 일자
      `DJ${catId.padEnd(10, '0')}` + FS + //#20 #21 CAT ID
      FS + FS + // #32 #33 SignData
      `NIC` + `KPS${filterData ?? ''}` + FS // #36 #37 부가정보
    );
  }

  return (
    serviceCode + FS +
    FS + FS +
    formattedInstallments + FS +
    FS + FS +
    totalAmount + FS +
    (approvalNumber || '') + FS +
    (originalDate || '') + FS +
    FS + FS
  );
};

const sendPostRequest = async (sendbuf: string): Promise<string> => {
  const daemonUrl = `http://localhost:9088`;

  // EUC-KR 또는 CP949 인코딩 (실제로는 CP949가 대부분 환경에서 EUC-KR 역할을 함)
  const encodedBody = iconv.encode(sendbuf, 'euc-kr');

  const response = await fetch(daemonUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded; charset=euc-kr',
    },
    body: new Uint8Array(encodedBody), // Fetch는 ArrayBuffer or TypedArray 사용 가능
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.text();
};

// Hook Implementation
const useNicePayModule = () => {
  const [data, setData] = useState<ParsedResponse>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>();

  const log = (message: string, ...optionalParams: any[]) => {
    console.log(`[NicePayModule]: ${message}`, ...optionalParams);
  };

  const execute = async (
    type: 'approve' | 'cancel',
    orderId: number,
    totalAmount: number,
    installmentMonths: string,
    taxAmount: number = 0,
    approvalNumber?: string,
    originalDate?: string,
    catId?: string,
  ) => {
    setData(undefined);
    setError(undefined);
    setIsLoading(true);

    try {
      let sendbuf: string;

      log('Transaction Execution Started', {
        type,
        totalAmount,
        installmentMonths,
        taxAmount,
        approvalNumber,
        originalDate,
        catId,
        orderId,
      });

      const { data: res } = await getFilterData(orderId);

      if (type === 'approve') {
        sendbuf = createTransactionMessage('D1', totalAmount, installmentMonths, taxAmount, '', '', res?.data || '', catId);
      } else if (type === 'cancel') {
        sendbuf = createTransactionMessage('D2', totalAmount, installmentMonths, taxAmount, approvalNumber || '', originalDate || '', res?.data || '', catId);
      } else {
        throw new Error('Invalid transaction type');
      }

      log('Raw Send Data Created', { sendbuf });

      const formattedBuf = makeSendData('PCAT', sendbuf);
      log('Formatted Send Buffer Created', { formattedBuf });

      const response = await sendPostRequest(formattedBuf);
      log('Raw Response Received', { response });

      const parsed = parseResponse(type, orderId, sendbuf, response);
      log('Parsed Response', parsed);

      setData(parsed);

      if (!parsed.success) {
        throw new Error(parsed.rejectMessage2 ?? parsed.message);
      }

      return parsed;
    } catch (err) {
      log('Error Occurred', err);
      setError(err instanceof Error && err?.message ? err.message : '결제 실패');
      throw err;
    } finally {
      log('Transaction Execution Finished');
      setIsLoading(false);
    }
  };

  const clear = () => {
    log('Clearing Data State');
    setData(undefined);
  };

  return {
    data,
    isLoading,
    error,
    execute,
    clear,
  };
};

export default useNicePayModule;
